import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad de la carpeta Cauciones
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

// --- HELPERS INTERNOS DE PROCESAMIENTO ---
const parseToNumeric = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    const clean = String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    return parseFloat(clean) || 0;
};

const getImportePriority = (monto, moneda = '$') => {
    const val = parseToNumeric(monto);
    if (moneda === 'USD') return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-200', val: 1 };
    if (val > 300000) return { label: 'P1', class: 'bg-red-200 text-red-900 border-red-200', val: 1 };
    if (val >= 100000) return { label: 'P2', class: 'bg-orange-200 text-orange-900 border-orange-200', val: 2 };
    return { label: 'P3', class: 'bg-yellow-100 text-yellow-900 border-yellow-200', val: 3 };
};

/**
 * useRefacturacionesTable - Hook de negocio unificado para el Tablero de Refacturaciones.
 */
export const useRefacturacionesTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [rawEndosos, setRawEndosos] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [activeItem, setActiveItem] = useState(null);
    const [activePolizaData, setActivePolizaData] = useState(null);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache, policiesCache } = useDataHydration(rawEndosos);

    // --- 2. LISTENER DE FIRESTORE EN TIEMPO REAL Y REDUCCIÓN EN CALIENTE ---
    useEffect(() => {
        const q = query(collectionGroup(db, 'endosos'));
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const raw = snap.docs.map(d => {
                const data = d.data();
                const parts = d.ref.path.split('/');
                const estadoReal = (data.estadoRefacturacion || data.estadoGestionRenovacion || 'PENDIENTE').toUpperCase().trim();
                
                return { 
                    id: d.id, 
                    path: d.ref.path,
                    clienteId: parts[3],
                    polizaId: parts[5],
                    ...data,
                    estadoRefacturacion: estadoReal 
                };
            }).filter(d => d.tipoGestion === 'Refacturacion'); 

            // Agrupación en caliente por póliza
            const policyGroups = new Map();
            raw.forEach(rec => {
                if (!policyGroups.has(rec.polizaId)) policyGroups.set(rec.polizaId, []);
                policyGroups.get(rec.polizaId).push(rec);
            });

            const processed = [];
            const clientPoliciesUnpaid = new Map();

            policyGroups.forEach((group, polizaId) => {
                const groupFiltered = group.filter(e => !['ANULADA', 'INCOBRABLE'].includes(e.estadoRefacturacion));
                if (groupFiltered.length === 0) return;

                const sortedDesc = [...groupFiltered].sort((a, b) => (Number(b.numeroEndoso) || 0) - (Number(a.numeroEndoso) || 0));
                const unpaidItems = groupFiltered.filter(e => !['ABONADO', 'ABONADO - FALTA RECIBO'].includes(e.estadoRefacturacion));

                if (unpaidItems.length > 0) {
                    const cId = groupFiltered[0].clienteId;
                    if (!clientPoliciesUnpaid.has(cId)) clientPoliciesUnpaid.set(cId, new Set());
                    clientPoliciesUnpaid.get(cId).add(polizaId);
                    
                    unpaidItems.forEach(item => {
                        processed.push({ 
                            ...item, 
                            isFromDebt: true, 
                            pendientesMismaPoliza: unpaidItems.length - 1 
                        });
                    });
                } else {
                    const absoluteLast = sortedDesc[0];
                    const today = new Date(); today.setHours(0,0,0,0);
                    const expDate = absoluteLast.finVigencia ? new Date(absoluteLast.finVigencia) : null;
                    if (expDate) expDate.setHours(0,0,0,0);
                    
                    const daysToExpiration = expDate ? (expDate - today) / (1000 * 60 * 60 * 24) : 999;
                    if (daysToExpiration <= 45) {
                        processed.push({ 
                            ...absoluteLast, 
                            isUrgentRenovacion: true, 
                            isExpired: daysToExpiration < 0, 
                            pendientesMismaPoliza: 0 
                        });
                    }
                }
            });

            // Mapeamos deudas múltiples a nivel de cliente antes de guardar en estado
            const finalData = processed.map(p => {
                const otherUnpaidPolicies = clientPoliciesUnpaid.get(p.clienteId) || new Set();
                return { 
                    ...p, 
                    tieneDeudaMultiple: otherUnpaidPolicies.size > 1, 
                    packCount: otherUnpaidPolicies.size - 1 
                };
            });

            setRawEndosos(finalData);
            setLoading(false);
        }, (error) => console.error("Error cargando refacturaciones:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return rawEndosos.map(e => {
            const pKey = `${e.clienteId}|${e.polizaId}`;
            const p = policiesCache[pKey] || {};
            const c = clientsCache[e.clienteId] || {};

            let displayNombre = `${e.clienteNombre || p.clienteNombre || ''} ${e.clienteApellido || p.clienteApellido || ''}`.trim();
            if (!displayNombre) {
                displayNombre = c.persona === 'JURIDICA' ? (c.nombre || '') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
            }
            
            const displayDoc = e.clienteCuit || e.clienteDni || p.clienteCuit || p.clienteDni || c.cuit || c.dni || '-';
            const canalNombreDisplay = e.canalNombre || p.canalNombre || e.subcanal || p.subcanal || '-';

            return {
                ...e,
                displayNombre: (displayNombre || 'S/D').toUpperCase(),
                displayDoc,
                compania: e.compania || p.compania || 'S/C',
                riesgo: e.riesgo || p.riesgo || 'S/R',
                subriesgo: e.subriesgo || p.subriesgo || '',
                nPoliza: e.nPoliza || p.nPoliza || 'S/N',
                formaPago: e.formaPago || p.formaPago || '-',
                canal: e.canal || p.canal || '-',
                canalNombreDisplay,
                prioridadVal: getImportePriority(e.primaEmitida, e.moneda).val,
                importeDeuda: parseToNumeric(e.primaEmitida), // Seteamos numérico puro para sorting automático
                rootEstado: (p.estadoEmision || p.estadoPoliza || '').toUpperCase().trim(),
                estadoRefacturacion: e.estadoRefacturacion 
            };
        }).filter(item => item.rootEstado !== 'ANULADA');
    }, [rawEndosos, clientsCache, policiesCache]);

    // --- ◄ MEJORA CORRECCIÓN DE PARPADEO: Cálculo Síncrono de Dependencias Faltantes ---
    const needsHydration = useMemo(() => {
        if (rawEndosos.length === 0) return false;
        return rawEndosos.some(item => {
            const clientId = item.clienteId;
            const polizaId = item.polizaId;
            const pKey = `${clientId}|${polizaId}`;
            
            const hasMissingClient = clientId && !clientsCache[clientId];
            const hasMissingPolicy = clientId && polizaId && !policiesCache[pKey];
            
            return hasMissingClient || hasMissingPolicy;
        });
    }, [rawEndosos, clientsCache, policiesCache]);

    // Combinación transaccional de carga: Carga si el listener está activo O si faltan datos por resolver
    const combinedLoading = useMemo(() => {
        return loading || needsHydration;
    }, [loading, needsHydration]);

    // --- 4. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN (useTableState) ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'finVigencia', direction: 'ascending' },
        initialColumns: {
            displayNombre: true, displayDoc: true, nPoliza: true, numeroEndoso: true, compania: true,
            riesgo: true, finVigencia: true, importeDeuda: true, formaPago: true, 
            estadoRefacturacion: true, fechaReclamo: true, quienGestiono: true, canal: true
        },
        recordsPerPage: 100
    });

    // --- 5. ENLACE DE PRIORIDAD DE RENOVACIONES CRÍTICAS SOBRE EL ORDEN DE LA TABLA ---
    const sortedDataWithRenovations = useMemo(() => {
        const baseSorted = tableState.sortedData;
        return [...baseSorted].sort((a, b) => {
            if (a.isUrgentRenovacion !== b.isUrgentRenovacion) {
                return a.isUrgentRenovacion ? -1 : 1;
            }
            return 0; // Mantiene el orden de tableState para el resto de elementos
        });
    }, [tableState.sortedData]);

    const paginatedData = useMemo(() => {
        const start = (tableState.currentPage - 1) * tableState.recordsPerPage;
        return sortedDataWithRenovations.slice(start, start + tableState.recordsPerPage);
    }, [sortedDataWithRenovations, tableState.currentPage, tableState.recordsPerPage]);

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear refacturación:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE CONTROLADORES DE ACCIÓN ---
    const handleAction = useCallback(async (item, type) => {
        const rawItem = rawEndosos.find(e => e.id === item.id);
        setActiveItem(rawItem);

        const pKey = `${item.clienteId}|${item.polizaId}`;
        const pData = policiesCache[pKey] || { id: item.polizaId, nPoliza: item.nPoliza, clienteId: item.clienteId, compania: item.compania };

        if (type === 'view_ref_history') { 
            setActivePolizaData(pData);
            setShowHistoryModal(true);
            activeItemPathRef.current = item.path;
            await updateDoc(doc(db, item.path), { 
                bloqueo: { userId: user?.uid, userName: profile?.userName || 'Usuario', timestamp: serverTimestamp() } 
            }).catch(() => { activeItemPathRef.current = null; });
        } else if (type === 'view_policy_detail') {
            setActivePolizaData(pData);
            setShowPolicyModal(true);
        } else if (type === 'view_client_ficha') {
            setShowFichaModal(true);
        }
    }, [rawEndosos, policiesCache, user, profile]);

    return {
        loading: combinedLoading, // ◄ INTERCEPCIÓN CLAVE: Retorna el estado combinado libre de destellos
        clientsCache,
        activeItem,
        activePolizaData,
        showPolicyModal,
        showFichaModal,
        showHistoryModal,
        tableState,
        itemsTable,
        paginatedData,
        activeItemPathRef,
        setShowPolicyModal,
        setShowFichaModal,
        setShowHistoryModal,
        setActiveItem,
        setActivePolizaData,
        handleAction,
        unlockPoliza
    };
};